/**
 * /agents/:id — the agent is the project: its experiments with a score column per
 * evaluator, the score over time, and the agent package (`?tab=package`). "New
 * experiment" opens the flow prefilled with this agent.
 */

import { useParams, useSearchParams } from "react-router";
import { Plus } from "lucide-react";
import { ButtonLink } from "@/components/ui/button";
import { Crumbs, PageHeader } from "@/components/ui/page";
import { Pill } from "@/components/ui/pill";
import { QueryState } from "@/components/ui/states";
import { Tabs } from "@/components/ui/tabs";
import { useExperiments } from "@/features/experiments/hooks";
import { AgentExperiments } from "./agent-experiments";
import { AgentPackage } from "./agent-package";
import { useAgent } from "./hooks";
import { ProjectAction } from "@/features/projects/project-action";

type Tab = "experiments" | "package";

/** `?tab=config` is the old name of the package tab; old links still land on it. */
const PACKAGE_TABS = ["package", "config"];

export function AgentPage() {
  const { id = "" } = useParams();
  const agent = useAgent(id);
  const experiments = useExperiments({ agent_id: id });
  const [params, setParams] = useSearchParams();
  const tab: Tab = PACKAGE_TABS.includes(params.get("tab") ?? "") ? "package" : "experiments";

  return (
    <QueryState query={agent} what="the agent">
      {agent.data && (
        <>
          <div className="flex flex-col gap-2.5">
            <Crumbs items={[{ label: "Agents", to: "/agents" }, { label: agent.data.name }]} />
            <PageHeader
              title={agent.data.name}
              badges={
                <>
                  <Pill>{agent.data.connector_label}</Pill>
                  <span className="src" title="Where it lives; any connection to this base URL can run it">
                    {agent.data.base_url}
                  </span>
                </>
              }
              actions={
                <ProjectAction permission="experiments:write">
                  <ButtonLink variant="primary" to={`/experiments/new?agent=${id}`}>
                    <Plus size={14} strokeWidth={2.2} aria-hidden />
                    New experiment
                  </ButtonLink>
                </ProjectAction>
              }
            />
          </div>
          <Tabs
            label="Agent sections"
            value={tab}
            onChange={(t) => setParams(t === "package" ? { tab: t } : {})}
            items={[
              { key: "experiments", label: "Experiments", count: experiments.data?.length },
              { key: "package", label: "Agent package" },
            ]}
          />
          {tab === "experiments" ? (
            <AgentExperiments agentId={id} experiments={experiments} />
          ) : (
            <AgentPackage agent={agent.data} />
          )}
        </>
      )}
    </QueryState>
  );
}
